/**
 * DirtyFormProvider — single global "unsaved changes?" modal + dirty-form
 * registry + centralized router-navigation blocker.
 *
 * What this gives you:
 *   - useDirtyForm().confirmDiscardAsync() opens a Mantine Modal and resolves
 *     to true (proceed / discard) or false (stay).
 *   - useDirtyForm().registerDirty(id, isDirty) lets any active form publish
 *     its dirty state into a single global flag, so non-form-aware code
 *     (e.g. logout) can call anyDirty() before navigating.
 *
 * The Modal styling intentionally diverges from Mantine defaults: a slightly
 * tinted body, primary-coloured accent border, brand sans-serif, and the two
 * buttons in clearly different colours so the user can't mis-click between
 * "stay" and "discard".
 *
 * Coverage today:
 *   - Tab close / hard reload / new URL: useDirtyFormGuard's beforeunload
 *     handler (browser-native dialog).
 *   - In-app router navigation (sidebar / back-forward / Link / navigate()):
 *     the centralized useBlocker below — fires confirmDiscardAsync(), then
 *     proceeds or resets based on the user's answer. Single registration
 *     app-wide (Task #258) avoids the StrictMode "router only supports one
 *     blocker" warning that occurred when each detail page registered its
 *     own per-hook blocker (Task #251 Stage 2 architecture).
 *   - Logout: AppShell's guardedLogout reads anyDirty() — logout doesn't go
 *     through router navigation so useBlocker can't see it.
 *   - Explicit in-page Cancel / Back buttons: the page calls
 *     confirmDiscard(onConfirm) directly via useDirtyFormGuard's return value.
 *
 * "Save & leave" (Pedro 2026-10-10, P13 batched checkboxes): a page may register a
 * SAVER with its dirty state (registerDirty's 3rd argument, or useDirtyFormGuard's
 * 2nd). When the router blocker fires and EVERY dirty form has a saver, the modal adds
 * a "Save & leave" button: it runs the savers, leaves if all succeed, and stays if one
 * fails (the page shows what failed). Pages without a saver keep the two-button modal.
 */
import { Button, Group, Modal, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  useState, type ReactNode,
} from 'react';
import { useBlocker } from 'react-router-dom';
import type { BlockerFunction } from 'react-router-dom';

interface PendingResolver { resolve: (v: boolean) => void; }

/** Commits a page's pending changes; resolves true when everything saved. */
export type DirtySaver = () => Promise<boolean>;

interface DirtyFormCtx {
  /** Promise resolves true if the user chooses Discard & leave — or, with
   *  `allowSave` and a saver for every dirty form, a successful Save & leave. */
  confirmDiscardAsync: (opts?: { allowSave?: boolean }) => Promise<boolean>;
  /** Register a form's dirty state (and optionally its saver) under a stable id;
   *  returns an unregister fn. */
  registerDirty: (id: string, isDirty: boolean, onSave?: DirtySaver) => () => void;
  /** Synchronous: is ANY currently-mounted form dirty? */
  anyDirty: () => boolean;
}

const Ctx = createContext<DirtyFormCtx | null>(null);

export function useDirtyForm(): DirtyFormCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('useDirtyForm must be used inside <DirtyFormProvider>');
  return c;
}

export function DirtyFormProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const pending = useRef<PendingResolver | null>(null);
  // Use a ref-backed Map so registration changes don't re-render the provider.
  const dirtyForms = useRef<Map<string, boolean>>(new Map());
  const savers = useRef<Map<string, DirtySaver>>(new Map());
  // Set when the modal opens: may it offer "Save & leave"? (see the docblock)
  const [saveable, setSaveable] = useState(false);
  const [saving, setSaving] = useState(false);

  const anyDirty = useCallback(() => {
    for (const v of dirtyForms.current.values()) if (v) return true;
    return false;
  }, []);

  const registerDirty = useCallback((id: string, isDirty: boolean, onSave?: DirtySaver) => {
    dirtyForms.current.set(id, isDirty);
    if (onSave) savers.current.set(id, onSave);
    else savers.current.delete(id);
    return () => { dirtyForms.current.delete(id); savers.current.delete(id); };
  }, []);

  const confirmDiscardAsync = useCallback((opts?: { allowSave?: boolean }): Promise<boolean> => {
    if (!anyDirty()) return Promise.resolve(true);
    const dirtyIds = [...dirtyForms.current].filter(([, d]) => d).map(([id]) => id);
    setSaveable(!!opts?.allowSave && dirtyIds.every((id) => savers.current.has(id)));
    setOpen(true);
    return new Promise((resolve) => { pending.current = { resolve }; });
  }, [anyDirty]);

  function close(answer: boolean) {
    setOpen(false);
    pending.current?.resolve(answer);
    pending.current = null;
  }

  // "Save & leave": run every dirty form's saver; leave only if all succeeded,
  // otherwise stay on the page (which reports what failed).
  async function saveAndLeave() {
    setSaving(true);
    let ok = true;
    const dirtyIds = [...dirtyForms.current].filter(([, d]) => d).map(([id]) => id);
    for (const id of dirtyIds) {
      const save = savers.current.get(id);
      if (!save || !(await save())) { ok = false; break; }
    }
    setSaving(false);
    close(ok);
  }

  // Task #258 (2026-06-04): centralized router-navigation blocker. One
  // registration app-wide, owned by this provider. The function form is
  // evaluated lazily per navigation attempt (not re-subscribed on every
  // render), so the StrictMode mount-unmount-remount cycle doesn't cause
  // duplicate registrations. Replaces per-hook useBlocker calls that
  // were in useDirtyFormGuard up to Task #251 Stage 2 — those produced
  // a "router only supports one blocker at a time" warning under
  // StrictMode because each detail page registered its own blocker.
  //
  // The shouldBlock function reads anyDirty() (which aggregates every
  // registered form's current dirty state via the dirtyForms ref). When
  // navigation across pathnames is attempted with anyDirty=true, the
  // blocker pauses and the useEffect below fires the existing confirm
  // modal flow. Same-pathname navigations (tab changes, query-string
  // updates) are explicitly excluded so they don't spuriously prompt.
  const blocker = useBlocker(
    useCallback<BlockerFunction>(
      ({ currentLocation, nextLocation }) =>
        anyDirty() && currentLocation.pathname !== nextLocation.pathname,
      [anyDirty],
    ),
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      void confirmDiscardAsync({ allowSave: true }).then((ok) => {
        if (ok) blocker.proceed();
        else blocker.reset();
      });
    }
  }, [blocker, confirmDiscardAsync]);

  const value = useMemo(
    () => ({ confirmDiscardAsync, registerDirty, anyDirty }),
    [confirmDiscardAsync, registerDirty, anyDirty],
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      <Modal
        opened={open}
        onClose={() => { if (!saving) close(false); }}
        centered
        withCloseButton={false}
        // Smoke 2026-10-11 (S9, Pedro): "md" so the three buttons sit on one line.
        size={saveable ? 'md' : 'sm'}
        radius="md"
        overlayProps={{ blur: 2, backgroundOpacity: 0.35 }}
        title={
          <Group gap="xs" align="center">
            <IconAlertTriangle size={18} color="var(--mantine-color-laranja-6)" />
            <Text fw={700} size="sm">Unsaved changes</Text>
          </Group>
        }
        styles={{
          // Same accent treatment as the centred toasts: a single coloured
          // side border, otherwise neutral. No tinted background.
          content: {
            borderLeft: '4px solid var(--mantine-color-laranja-6)',
            fontFamily: 'inherit',
          },
          header: { paddingBottom: 8 },
          body: { paddingTop: 4 },
        }}
      >
        <Stack gap="md">
          <Text size="sm" c="dimmed">
            {saveable
              ? 'You have unsaved changes on this page. Save them before you leave?'
              : 'You have unsaved changes on this page.'}
          </Text>
          {/* One line, same size, three weights: leaving without saving (red outline),
              staying (neutral), saving (filled) — smoke 2026-10-11 (S9). */}
          <Group justify="flex-end" gap="sm" wrap="nowrap">
            <Button color="bordeaux" variant="outline" size="sm"
              disabled={saving} onClick={() => close(true)}>
              Discard &amp; leave
            </Button>
            <Button color="azul" variant={saveable ? 'default' : 'filled'} size="sm"
              disabled={saving} onClick={() => close(false)}>
              Stay
            </Button>
            {saveable && (
              <Button color="azul" size="sm" loading={saving} onClick={() => void saveAndLeave()}>
                Save &amp; leave
              </Button>
            )}
          </Group>
        </Stack>
      </Modal>
    </Ctx.Provider>
  );
}
